import { DBTables } from "@utils/constants/dbTables";
import api from "./api";

export type QuotedSnapshot = {
  noteId: string;
  body: string;
  authorName: string;
  createdAt: string;
  // Recursive: when this snapshot itself was of a note that quoted another,
  // the chain travels with it so the UI can render the full ancestry.
  quotedSnapshot?: QuotedSnapshot | null;
};

export type Note = {
  id: string;
  referenceTable: DBTables;
  referenceId: string;
  subscriberId: string | null;
  level1ResourceId: string | null;
  postedByUserId: string;
  note: string;
  isReportable: boolean;
  isArchived: boolean;
  quotedSnapshot: QuotedSnapshot | null;
  chainTransactionId: string | null;
  createdAt: string;
  updatedAt: string;
  postedByUser?: { id: string; name: string };
  subscriber?: { id: string; name: string };
  evidences?: Array<{ id: string; fileName: string }>;
  // Human-readable identifier of the anchor entity, set by the consolidated
  // feeds (Scheme / Customer Audit Trail) so a row can show what it's posted on.
  entityLabel?: string | null;
};

export type NoteListParams = {
  referenceTable?: DBTables;
  referenceId?: string;
  subscriberId?: string;
  level1ResourceId?: string;
  postedByUserId?: string;
  fromDate?: Date;
  toDate?: Date;
  keyword?: string;
  includeArchived?: boolean;
  reportableOnly?: boolean;
};

// Date-input ("YYYY-MM-DD") → local day boundaries for the fromDate/toDate
// filters. `new Date("YYYY-MM-DD")` alone parses as UTC midnight, which shifts
// the boundary by the timezone offset and makes a same-day from/to range empty
// (both ends landed on the same instant).
export const dateInputToStartOfDay = (d: string): Date => new Date(`${d}T00:00:00`);
export const dateInputToEndOfDay = (d: string): Date => new Date(`${d}T23:59:59.999`);

export type NotesPaginationMeta = {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  firstPage: number;
};

export type PaginatedNotes = {
  notes: Note[];
  meta: NotesPaginationMeta;
};

export type NoteListPaginatedParams = NoteListParams & {
  page: number;
  pageLimit?: number;
};

/**
 * Non-paginated fetch. The backend caps responses at `pageLimit` (default 20) even when
 * pagination params are omitted, so this returns the most-recent page of notes. Suitable
 * for v1 callers; new code should prefer `getNotesPaginated`.
 */
export const getNotes = async (params: NoteListParams = {}): Promise<Note[]> => {
  const { data } = await api.get("/admin/notes", { params });

  return data.notes;
};

/** Paginated fetch for the new NotesPanel feed. */
export const getNotesPaginated = async (
  params: NoteListPaginatedParams,
): Promise<PaginatedNotes> => {
  const { data } = await api.get("/admin/notes", { params });

  return { notes: data.notes, meta: data.meta };
};

// POST: listings on big schemes can have 400+ rows, which blows past the
// ~8 KB URL limit on GET and silently drops referenceIds from the response.
// Switching to POST removes the limit entirely.
export const getNoteCounts = async (
  referenceTable: DBTables,
  referenceIds: string[],
): Promise<Record<string, number>> => {
  if (!referenceIds.length) return {};

  const { data } = await api.post("/admin/notes/counts", {
    referenceTable,
    referenceIds,
  });

  return data.counts ?? {};
};

// Counts every note touching a subscriber/scheme via the denormalised rollup
// columns — the same view the NotesPanel renders on the Subscriber/Scheme
// audit-trail Notes tab. Listing-row badges use this so the count matches what
// the user sees after clicking through.
export const getNoteRollupCounts = async (
  rollupBy: "subscriber" | "scheme",
  rollupIds: string[],
): Promise<Record<string, number>> => {
  if (!rollupIds.length) return {};

  const { data } = await api.post("/admin/notes/counts", {
    rollupBy,
    rollupIds,
  });

  return data.counts ?? {};
};

export type AddNotePayload = {
  referenceTable: DBTables;
  referenceId: string;
  note: string;
  isReportable?: boolean;
  parentNoteId?: string;
  attachment?: File;
};

export const addNote = async (payload: AddNotePayload): Promise<Note> => {
  let body: FormData | AddNotePayload = payload;

  if (payload.attachment) {
    const formData = new FormData();
    formData.append("referenceTable", payload.referenceTable);
    formData.append("referenceId", payload.referenceId);
    formData.append("note", payload.note);
    if (payload.isReportable !== undefined) {
      formData.append("isReportable", String(payload.isReportable));
    }
    if (payload.parentNoteId) {
      formData.append("parentNoteId", payload.parentNoteId);
    }
    formData.append("attachment", payload.attachment);
    body = formData;
  }

  const { data } = await api.post("/admin/notes", body);

  return data.note;
};

export const archiveNote = async (id: string): Promise<Note> => {
  const { data } = await api.patch(`/admin/notes/${id}/archive`);

  return data.note;
};

export const unarchiveNote = async (id: string): Promise<Note> => {
  const { data } = await api.patch(`/admin/notes/${id}/unarchive`);

  return data.note;
};

export const setNoteReportable = async (
  id: string,
  isReportable: boolean,
): Promise<Note> => {
  const { data } = await api.patch(`/admin/notes/${id}/reportable`, {
    isReportable,
  });

  return data.note;
};

export type NoteEntityRow = {
  referenceTable: DBTables;
  referenceId: string;
  identifier: string;
  level1ResourceId: string | null;
  schemeName: string | null;
  noteCount: number;
  reportableCount: number;
  lastNoteAt: string;
};

export type NoteEntityListParams = {
  page: number;
  pageLimit?: number;
  level1ResourceId?: string;
  referenceTable?: DBTables;
  hasReportable?: boolean;
  fromDate?: Date;
  toDate?: Date;
};

export type PaginatedNoteEntities = {
  entities: NoteEntityRow[];
  meta: NotesPaginationMeta;
};

export const getNoteEntities = async (
  params: NoteEntityListParams,
): Promise<PaginatedNoteEntities> => {
  const { data } = await api.get("/admin/notes/entities", { params });
  return { entities: data.entities, meta: data.meta };
};
